import type { Timestamp } from '@bufbuild/protobuf/wkt'
import {
  ApprovalMode,
  BusinessProcess,
} from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import { type StageLocal, lacksApprovers } from '~/components/approval/stage'
import {
  type CellKey,
  makeNoSectionCellKey,
} from '~/pages/AdminApprovalFlow/constants/processMap'

/** 1 セルのフロントエンド表現 */
export type CellLocal = {
  mode: ApprovalMode
  stages: StageLocal[]
}

/**
 * 編集前のサーバースナップショット。
 * RESET_PROCESS でユーザー編集を巻き戻す対象 (= cells/updatedAt)。
 */
export type ProcessSnapshot = {
  cells: ReadonlyMap<CellKey, CellLocal>
  updatedAt: Timestamp | undefined
}

/**
 * 1 プロセスの編集ステート。
 * cells は CellKey → CellLocal のマップ。未設定セルはマップに含まれない (= mode=UNSPECIFIED 扱い)。
 */
export type ProcessState = {
  businessProcess: BusinessProcess
  /** CellKey → CellLocal。未設定セルは absent (= mode UNSPECIFIED) */
  cells: ReadonlyMap<CellKey, CellLocal>
  updatedAt: Timestamp | undefined
  /** サーバースナップショットとの diff がある場合 true */
  dirty: boolean
  /**
   * 編集前のサーバースナップショット。
   * INIT_PROCESS / MARK_SAVED で確定し、RESET_PROCESS でここへ巻き戻す。
   */
  snapshot: ProcessSnapshot
}

export type MatrixState = ReadonlyMap<BusinessProcess, ProcessState>

// ---- Actions ----

type SetCellModeAction = {
  type: 'SET_CELL_MODE'
  businessProcess: BusinessProcess
  key: CellKey
  mode: ApprovalMode
}

type SetCellStagesAction = {
  type: 'SET_CELL_STAGES'
  businessProcess: BusinessProcess
  key: CellKey
  stages: StageLocal[]
}

/** プロセスの全エリアのセルを同じモードで上書きする (一括セレクト)。 */
type BulkSetAllAction = {
  type: 'BULK_SET_ALL'
  businessProcess: BusinessProcess
  areas: readonly AreaCode[]
  mode: ApprovalMode
}

type CopyToOtherAreasAction = {
  type: 'COPY_TO_OTHER_AREAS'
  businessProcess: BusinessProcess
  srcKey: CellKey
  destAreas: readonly AreaCode[]
}

type ResetProcessAction = {
  type: 'RESET_PROCESS'
  businessProcess: BusinessProcess
}

type MarkSavedAction = {
  type: 'MARK_SAVED'
  businessProcess: BusinessProcess
  /** Save レスポンスの updated_at で上書き */
  updatedAt: Timestamp | undefined
}

type InitProcessAction = {
  type: 'INIT_PROCESS'
  processState: ProcessState
}

export type MatrixAction =
  | SetCellModeAction
  | SetCellStagesAction
  | BulkSetAllAction
  | CopyToOtherAreasAction
  | ResetProcessAction
  | MarkSavedAction
  | InitProcessAction

// ---- Helpers ----

function setCell(
  cells: ReadonlyMap<CellKey, CellLocal>,
  key: CellKey,
  patch: Partial<CellLocal>,
): Map<CellKey, CellLocal> {
  const next = new Map(cells)
  const existing = next.get(key) ?? {
    mode: ApprovalMode.UNSPECIFIED,
    stages: [],
  }
  next.set(key, { ...existing, ...patch })
  return next
}

function setCells(
  cells: ReadonlyMap<CellKey, CellLocal>,
  keys: CellKey[],
  mode: ApprovalMode,
): Map<CellKey, CellLocal> {
  const next = new Map(cells)
  for (const key of keys) {
    if (mode === ApprovalMode.UNSPECIFIED) {
      next.delete(key)
    } else {
      const existing = next.get(key) ?? {
        mode: ApprovalMode.UNSPECIFIED,
        stages: [],
      }
      const stages = mode === ApprovalMode.APPROVAL ? existing.stages : []
      next.set(key, { ...existing, mode, stages })
    }
  }
  return next
}

/**
 * cells マップ間に実 diff があるか (更新前後で dirty を立てるべき変更があるか)。
 * 等価判定は isCellChanged が SoT (未設定セルは mode=UNSPECIFIED / stages=[] 扱い)。
 */
function cellsChanged(
  before: ReadonlyMap<CellKey, CellLocal>,
  after: ReadonlyMap<CellKey, CellLocal>,
): boolean {
  for (const key of before.keys()) {
    if (isCellChanged(before.get(key), after.get(key))) return true
  }
  for (const key of after.keys()) {
    if (!before.has(key) && isCellChanged(undefined, after.get(key))) {
      return true
    }
  }
  return false
}

/**
 * 対象 process を updater で更新する。
 *
 * dirty をいつ立てるかの不変条件はここが唯一の権威: 更新前後で cells に
 * 実 diff があるときだけ `dirty: true` を立て、 実 diff が無ければ state をそのまま返して
 * dirty を据え置く (= no-op を吸収する)。
 *
 * DS Select の `emitOnReselect` により一括セレクトは同値再選択でも onChange を発火する。
 * 呼び出し側に「同値なら dirty にしない」事前ガードを持たせると
 * 1 箇所の漏れで保存ボタン・離脱ガードが誤起動するため、 その不変条件は reducer が持つ。
 * 呼び出し側は無条件に dispatch してよい。
 */
function updateProcess(
  state: MatrixState,
  businessProcess: BusinessProcess,
  updater: (p: ProcessState) => ProcessState,
): MatrixState {
  const proc = state.get(businessProcess)
  if (proc === undefined) return state
  const updated = updater(proc)
  if (!cellsChanged(proc.cells, updated.cells)) {
    // 実 diff なし (同値再選択・src 欠落 copy 等) → dirty を立てず state を据え置く
    return state
  }
  const next = new Map(state)
  next.set(businessProcess, { ...updated, dirty: true })
  return next
}

// ---- Reducer ----

export function matrixReducer(
  state: MatrixState,
  action: MatrixAction,
): MatrixState {
  switch (action.type) {
    case 'INIT_PROCESS': {
      const next = new Map(state)
      next.set(action.processState.businessProcess, action.processState)
      return next
    }

    case 'SET_CELL_MODE': {
      return updateProcess(state, action.businessProcess, (proc) => {
        const nextCells =
          action.mode === ApprovalMode.UNSPECIFIED
            ? (() => {
                const m = new Map(proc.cells)
                m.delete(action.key)
                return m
              })()
            : setCell(proc.cells, action.key, {
                mode: action.mode,
                stages:
                  action.mode === ApprovalMode.APPROVAL
                    ? (proc.cells.get(action.key)?.stages ?? [])
                    : [],
              })
        return { ...proc, cells: nextCells }
      })
    }

    case 'SET_CELL_STAGES': {
      return updateProcess(state, action.businessProcess, (proc) => {
        const nextCells = setCell(proc.cells, action.key, {
          stages: action.stages,
        })
        return { ...proc, cells: nextCells }
      })
    }

    case 'BULK_SET_ALL': {
      return updateProcess(state, action.businessProcess, (proc) => {
        const keys: CellKey[] = action.areas.map((area) =>
          makeNoSectionCellKey(action.businessProcess, area),
        )
        return { ...proc, cells: setCells(proc.cells, keys, action.mode) }
      })
    }

    case 'COPY_TO_OTHER_AREAS': {
      return updateProcess(state, action.businessProcess, (proc) => {
        const srcCell = proc.cells.get(action.srcKey)
        if (srcCell === undefined) return proc
        const next = new Map(proc.cells)
        for (const area of action.destAreas) {
          const destKey = makeNoSectionCellKey(action.businessProcess, area)
          if (destKey !== action.srcKey) {
            next.set(destKey, {
              ...srcCell,
              stages: srcCell.stages.map((s) => ({
                ...s,
                approverMemberIds: [...s.approverMemberIds],
              })),
            })
          }
        }
        return { ...proc, cells: next }
      })
    }

    case 'RESET_PROCESS': {
      const next = new Map(state)
      const proc = next.get(action.businessProcess)
      if (proc !== undefined) {
        // 編集前のサーバースナップショットへ巻き戻す (dirty フラグを落とすだけでなく実値を復元)
        next.set(action.businessProcess, {
          ...proc,
          cells: proc.snapshot.cells,
          updatedAt: proc.snapshot.updatedAt,
          dirty: false,
        })
      }
      return next
    }

    case 'MARK_SAVED': {
      const next = new Map(state)
      const proc = next.get(action.businessProcess)
      if (proc !== undefined) {
        next.set(action.businessProcess, {
          ...proc,
          updatedAt: action.updatedAt,
          dirty: false,
          // 保存成功時点の編集内容を新しいサーバースナップショットとして確定
          snapshot: {
            cells: proc.cells,
            updatedAt: action.updatedAt,
          },
        })
      }
      return next
    }

    default:
      return state
  }
}

// ---- Validation helpers ----

/** セルが「提出ブロック対象」かどうか = 未設定 or 要承認+承認者未設定 */
export function isCellIncomplete(cell: CellLocal | undefined): boolean {
  if (cell === undefined) return true // 未設定
  if (cell.mode === ApprovalMode.UNSPECIFIED) return true // 未設定
  // 要承認+承認者未設定
  return lacksApprovers(cell.mode === ApprovalMode.APPROVAL, cell.stages)
}

/**
 * 「要承認」セルの登録済承認者に候補不在の id が含まれるか (= eauth 上で削除・無効化
 * された承認者を含む「欠落」状態、AC-APF-026d)。
 *
 * 候補リスト (`ListActiveMembers`) は非アクティブ (削除・無効化) を除外して返すため、
 * 登録済 `approverMemberId` が candidateIds に不在 ⟺ 削除・無効化済み。未設定
 * (`isCellIncomplete`) とは別概念 — id はあるが無効、なので保存はブロックしない
 * (Design Note D2: 警告のみ)。
 */
export function hasMissingApprover(
  cell: CellLocal | undefined,
  candidateIds: ReadonlySet<string>,
): boolean {
  if (cell === undefined || cell.mode !== ApprovalMode.APPROVAL) return false
  return cell.stages.some((s) =>
    s.approverMemberIds.some((id) => !candidateIds.has(id)),
  )
}

/**
 * セルが保存時点スナップショットから変更されているか (AC-011a)。
 * mode と stages (戦略 + 承認者リスト) の差分を見る。
 * 未設定セルは mode=UNSPECIFIED / stages=[] として比較する。
 */
export function isCellChanged(
  current: CellLocal | undefined,
  snapshot: CellLocal | undefined,
): boolean {
  const curMode = current?.mode ?? ApprovalMode.UNSPECIFIED
  const snapMode = snapshot?.mode ?? ApprovalMode.UNSPECIFIED
  if (curMode !== snapMode) return true

  const curStages = current?.stages ?? []
  const snapStages = snapshot?.stages ?? []
  if (curStages.length !== snapStages.length) return true
  for (let i = 0; i < curStages.length; i++) {
    const a = curStages[i]
    const b = snapStages[i]
    if (a.strategy !== b.strategy) return true
    if (a.approverMemberIds.length !== b.approverMemberIds.length) return true
    for (let j = 0; j < a.approverMemberIds.length; j++) {
      if (a.approverMemberIds[j] !== b.approverMemberIds[j]) return true
    }
  }
  return false
}

/** プロセスの incomplete セル数をカウント */
export function countIncompleteCells(
  proc: ProcessState,
  allKeys: CellKey[],
): number {
  return allKeys.filter((k) => isCellIncomplete(proc.cells.get(k))).length
}

/** 全セル設定済み (= 保存可能) */
export function canSave(proc: ProcessState, allKeys: CellKey[]): boolean {
  return countIncompleteCells(proc, allKeys) === 0
}

// ---- Cell key generators for a process ----

export function getAllCellKeys(
  businessProcess: BusinessProcess,
  areas: readonly AreaCode[],
): CellKey[] {
  return areas.map((a) => makeNoSectionCellKey(businessProcess, a))
}

/**
 * 複数セルのモードを集約する (プロセス全体の一括セレクト表示値の導出)。
 * 全キーが同じ非 UNSPECIFIED モードなら そのモードを返す。
 * 混在 or 全て UNSPECIFIED なら UNSPECIFIED (= 個別設定として扱う) を返す。
 */
export function aggregateMode(
  cells: ReadonlyMap<CellKey, CellLocal>,
  keys: readonly CellKey[],
): ApprovalMode {
  if (keys.length === 0) return ApprovalMode.UNSPECIFIED
  const first = cells.get(keys[0])?.mode ?? ApprovalMode.UNSPECIFIED
  for (const k of keys) {
    if ((cells.get(k)?.mode ?? ApprovalMode.UNSPECIFIED) !== first) {
      return ApprovalMode.UNSPECIFIED
    }
  }
  return first
}
